import {
  Badge,
  Box,
  IconButton,
  ListItem,
  ListItemButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { lightNeutral } from "src/colors";
import { useSettings } from "src/contexts/settings-context";
import { formatNotificationDate } from "../../utils/format-notification-date";
import {
  NotificationI,
  NotificationMetadataKind,
} from "../../types/responses/notification.response";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
  notification: NotificationI;
  onNotificationMarkAsRead: (token: string) => void;
  onNotificationMarkAsUnread: (token: string) => void;
  isChangingReadStatusDisabled: boolean;
  handleClose: () => void;
};

export default function RecentNotificationListItem({
  notification,
  onNotificationMarkAsRead,
  onNotificationMarkAsUnread,
  isChangingReadStatusDisabled,
  handleClose,
}: Props) {
  const { settings } = useSettings();

  const navigate = useNavigate();

  const handleNotificationStatusChange = useCallback(() => {
    if (notification.isRead) {
      onNotificationMarkAsUnread(notification.token);
    } else {
      onNotificationMarkAsRead(notification.token);
    }
  }, [notification]);

  const handleNotificationClick = useCallback(
    (notification: NotificationI) => {
      handleClose();
      switch (notification.metadata.kind) {
        case NotificationMetadataKind.BLACKLISTED_VEHICLE_LICENSE_PLATE_CREATED: {
          navigate(`/employees/${notification.metadata.employeeToken}`);
          return;
        }
        case NotificationMetadataKind.ORGANIZATION_SPOTS_ASSIGNED: {
          navigate(`/resource/spots`);
          return;
        }

        case NotificationMetadataKind.EMPLOYEE_CREATED_ISSUE: {
          navigate(`/issues/${notification.metadata.issueToken}`);
          return;
        }

        case NotificationMetadataKind.SITE_REMOVAL_APPROVAL_NEEDED:
        case NotificationMetadataKind.SITE_REMOVAL_CANCELLED:
          navigate("/site-management");
          return;

        case NotificationMetadataKind.TENANT_INVITATION_ACCEPTED:
          navigate("/tenants/invitations");
          return;

        case NotificationMetadataKind.TENANT_REMOVED:
          navigate("/tenants");
          return;

        default:
          navigate("/notifications");
      }
    },
    [navigate, handleClose],
  );

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => {
          if (!notification.isRead) {
            handleNotificationStatusChange();
          }
          handleNotificationClick(notification);
        }}
        disabled={isChangingReadStatusDisabled}
        sx={{
          px: 0,
          py: 0,
          flexDirection: "column",
          alignItems: "start",
          gap: 0.5,
          backgroundColor: !notification.isRead ? `action.hover` : "",
          "&:hover": {
            backgroundColor: notification.isRead ? "transparent" : "action.hover",
          },
          transition: "opacity 0.3s ease, background-color 0.3s ease, color 0.3s ease",
          "&:hover .MuiIconButton-root": {
            borderColor: `${lightNeutral[300]}`,
          },
        }}>
        <Box
          sx={{
            mx: 3,
            py: 2,
            width: "calc(100% - 48px)",
            borderBottom: `1px solid ${lightNeutral[300]}`,
          }}>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" fontWeight={500}>
              {notification.title}
            </Typography>

            <Tooltip
              key={`${notification.title}-${notification.isRead ? "read" : "unread"}`}
              title={
                notification.isRead
                  ? t("notifications:common.markAsUnread")
                  : t("notifications:common.markAsRead")
              }
              placement="bottom-end">
              <IconButton
                disabled={isChangingReadStatusDisabled}
                onClick={e => {
                  e.stopPropagation();
                  handleNotificationStatusChange();
                }}
                size="large"
                sx={{
                  border: "1px solid transparent",
                  borderRadius: "100%",
                  width: 24,
                  height: 24,
                  transition:
                    "border-color 0.3s ease, opacity 0.3s ease, background-color 0.3s ease, color 0.3s ease",
                }}>
                <Badge
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: !notification.isRead ? "#6EBAD3" : lightNeutral[300],
                      transition:
                        "border-color 0.3s ease, opacity 0.3s ease, background-color 0.3s ease, color 0.3s ease",
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {notification.content}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: 13 }}>
            {formatNotificationDate(new Date(notification.createdAt), settings.language)}
          </Typography>
        </Box>
      </ListItemButton>
    </ListItem>
  );
}
